import { APIFacetField } from '@common/types/queryState';
import { useMemo } from 'react';
import { useFacetingContext } from '../context';
import { AggregatedGroup } from '../types';
import { useFacetController } from './useFacetController';

export const useAggregatedFacets = (facetKey: string) => {
    const { getNormalizer, totalResults, realTimeMatches } =
        useFacetingContext();

    const { apiFacet, realTimeFacet } = useFacetController<'boolean'>(facetKey);

    const normalizer = useMemo(() => {
        return getNormalizer(facetKey);
    }, [facetKey, getNormalizer]);

    return useMemo(() => {
        const createEntries = (
            source: APIFacetField | undefined,
            field: 'total' | 'matches',
        ) =>
            Object.entries(source?.values ?? {}).map(([rawKey, count]) => ({
                rawKey,
                count,
                id: normalizer(rawKey),
                field,
            }));

        const allEntries = [
            ...createEntries(apiFacet, 'total'),
            ...createEntries(realTimeFacet, 'matches'),
        ];

        const initialState = {
            groups: {} as Record<string, AggregatedGroup>,
            totalSum: 0,
            matchesSum: 0,
        };

        const aggregated = allEntries.reduce(
            (acc, { rawKey, count, id, field }) => {
                const isTotal = field === 'total';
                const totalSum = acc.totalSum + (isTotal ? count : 0);
                const matchesSum = acc.matchesSum + (isTotal ? 0 : count);

                if (id === 'missing') return { ...acc, totalSum, matchesSum };

                const prevGroup = acc.groups[id] || {
                    id,
                    total: 0,
                    matches: 0,
                    rawKeys: [],
                };

                const rawKeys = prevGroup.rawKeys.includes(rawKey)
                    ? prevGroup.rawKeys
                    : [...prevGroup.rawKeys, rawKey];

                return {
                    totalSum,
                    matchesSum,
                    groups: {
                        ...acc.groups,
                        [id]: {
                            ...prevGroup,
                            total: prevGroup.total + (isTotal ? count : 0),
                            matches: prevGroup.matches + (!isTotal ? count : 0),
                            rawKeys,
                        },
                    },
                };
            },
            initialState,
        );

        const missingCounts = {
            total: Math.max(0, totalResults - aggregated.totalSum),
            matches: Math.max(0, realTimeMatches - aggregated.matchesSum),
        };

        return {
            groupsById: aggregated.groups,
            normalizer,
            missingCounts,
        };
    }, [apiFacet, normalizer, realTimeFacet, realTimeMatches, totalResults]);
};
